/**
 * Canonical step action titles (Getting Started + Steps 1–12).
 * Values are catalog keys (or shared ui.* keys); resolve via t() at runtime.
 */

import {
  SHARE_ACTION_TITLE_KEY,
  SHARE_DEFECTS_ACTION_TITLE_KEY,
} from './uiStrings';
import { t } from '../i18n/translate';
import {
  howToWorkTitle,
  readGuidanceTitle,
  readGreenBookTitle,
  understandStepTitle,
} from './stepActionTemplates';

/** Getting Started — keys into the English catalog (action 7 = shared share title). */
export const GETTING_STARTED_ACTION_LABELS: Record<number, string> = {
  1: 'getting_started.action.1.title',
  2: 'getting_started.action.2.title',
  3: 'getting_started.action.3.title',
  4: 'getting_started.action.4.title',
  5: 'getting_started.action.5.title',
  6: 'getting_started.action.6.title',
  7: SHARE_ACTION_TITLE_KEY,
  8: 'getting_started.action.8.title',
};

/** Non-templated titles as catalog keys (or shared ui.* keys). */
const UNIQUE_STEP_ACTION_TITLES: Record<number, Record<number, string>> = {
  1: {
    3: 'step.1.action.3.title',
    4: 'step.1.action.4.title',
    5: 'step.1.action.5.title',
    6: 'step.1.action.6.title',
  },
  2: {
    3: 'step.2.action.3.title',
    4: SHARE_ACTION_TITLE_KEY,
    5: 'step.2.action.5.title',
  },
  3: {
    3: 'step.3.action.3.title',
    4: 'step.3.action.4.title',
    5: SHARE_ACTION_TITLE_KEY,
    6: 'step.3.action.6.title',
  },
  4: {
    3: 'step.4.action.3.title',
    4: SHARE_ACTION_TITLE_KEY,
    5: 'step.4.action.5.title',
  },
  5: {
    3: 'step.5.action.3.title',
    4: 'step.5.action.4.title',
    5: SHARE_DEFECTS_ACTION_TITLE_KEY,
    6: 'step.5.action.6.title',
  },
  6: {
    3: 'step.6.action.3.title',
    4: SHARE_ACTION_TITLE_KEY,
    5: 'step.6.action.5.title',
  },
  7: {
    3: 'step.7.action.3.title',
    4: SHARE_ACTION_TITLE_KEY,
    5: 'step.7.action.5.title',
  },
  8: {
    3: 'step.8.action.3.title',
    4: SHARE_ACTION_TITLE_KEY,
    5: 'step.8.action.5.title',
  },
  9: {
    3: 'step.9.action.3.title',
    4: SHARE_ACTION_TITLE_KEY,
    5: 'step.9.action.5.title',
  },
  10: {
    3: 'step.10.action.3.title',
    4: SHARE_ACTION_TITLE_KEY,
    5: 'step.10.action.5.title',
  },
  11: {
    3: 'step.11.action.3.title',
    4: SHARE_ACTION_TITLE_KEY,
    5: 'step.11.action.5.title',
  },
  12: {
    3: 'step.12.action.3.title',
    4: SHARE_ACTION_TITLE_KEY,
    5: 'step.12.action.5.title',
  },
};

function resolveLabel(value: string | undefined): string {
  if (!value) return '';
  if (
    value.startsWith('ui.') ||
    value.startsWith('step.') ||
    value.startsWith('getting_started.')
  ) {
    return t(value);
  }
  return value;
}

function templatedTitle(stepNumber: number, actionId: number): string | undefined {
  if (actionId === 1) {
    if (stepNumber === 1 || stepNumber === 2 || stepNumber === 4) {
      return understandStepTitle(stepNumber);
    }
    if (stepNumber >= 3 && stepNumber <= 12) {
      return readGreenBookTitle(stepNumber);
    }
  }
  if (actionId === 2) {
    if (stepNumber >= 1 && stepNumber <= 9) {
      return howToWorkTitle(stepNumber);
    }
    if (stepNumber >= 10 && stepNumber <= 12) {
      return readGuidanceTitle(stepNumber);
    }
  }
  return undefined;
}

export function getStepActionLabelsForStep(stepNumber: number): Record<number, string> {
  if (stepNumber === 0) {
    const out: Record<number, string> = {};
    for (const [id, value] of Object.entries(GETTING_STARTED_ACTION_LABELS)) {
      out[Number(id)] = resolveLabel(value);
    }
    return out;
  }
  const unique = UNIQUE_STEP_ACTION_TITLES[stepNumber] ?? {};
  const labels: Record<number, string> = {};
  for (const [id, value] of Object.entries(unique)) {
    labels[Number(id)] = resolveLabel(value);
  }
  for (const actionId of [1, 2]) {
    const templated = templatedTitle(stepNumber, actionId);
    if (templated) labels[actionId] = templated;
  }
  return labels;
}

export function getStepActionLabel(stepNumber: number, actionId: number): string {
  if (stepNumber === 0) {
    return resolveLabel(GETTING_STARTED_ACTION_LABELS[actionId]) || `Action ${actionId}`;
  }
  const fromTemplate = templatedTitle(stepNumber, actionId);
  if (fromTemplate) return fromTemplate;
  return resolveLabel(UNIQUE_STEP_ACTION_TITLES[stepNumber]?.[actionId]) || `Action ${actionId}`;
}
